/**
 * GoogleCallback.jsx — SaaS override.
 *
 * Maneja el redirect de vuelta desde el backend tras el flujo OAuth2 de Google.
 * El backend redirige a /auth/google/callback?token=<access>&refresh=<refresh>
 *
 * Flujo:
 *  1. Lee token y refresh de los query params
 *  2. Llama a authLogin(token) — que almacena el token en localStorage
 *  3. Limpia la URL del navegador (evitar tokens en historial)
 *  4. Redirige a la invitación pendiente (sessionStorage) o al dashboard
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Center,
  Spinner,
  Text,
  VStack,
  Alert,
  AlertIcon,
  Button,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';

const PENDING_INVITE_KEY = 'pendingInviteToken';

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError(
        searchParams.get('error')
          ? `Error de autenticación: ${searchParams.get('error')}`
          : 'No se recibió el token de autenticación.'
      );
      return;
    }

    // Remove tokens from browser URL / history immediately
    window.history.replaceState(null, '', '/auth/google/callback');

    // Log in and navigate
    authLogin(token);

    const pendingToken = sessionStorage.getItem(PENDING_INVITE_KEY);
    setTimeout(() => {
      if (pendingToken) {
        navigate(`/invite?token=${encodeURIComponent(pendingToken)}`);
      } else {
        navigate('/');
      }
    }, 600);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <Center minH="100vh" bgGradient="linear(to-b, blue.800, blue.900)">
        <VStack spacing={4}>
          <Alert status="error" maxW="420px" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
          <Button
            variant="outline"
            colorScheme="whiteAlpha"
            color="white"
            borderColor="whiteAlpha.400"
            onClick={() => navigate('/login')}
          >
            Volver al inicio de sesión
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Center minH="100vh" bgGradient="linear(to-b, blue.800, blue.900)">
      <VStack spacing={4}>
        <Spinner size="xl" color="white" thickness="4px" speed="0.65s" />
        <Text color="white" fontWeight="medium">
          Completando inicio de sesión con Google…
        </Text>
      </VStack>
    </Center>
  );
};

export default GoogleCallback;
